/**
 * @fileoverview Componente responsável por exibir informações complementares do ticket.
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";

import { Avatar, CardHeader, IconButton, Tooltip, Box, useMediaQuery, useTheme } from "@material-ui/core";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import ReassignWhatsAppModal from "../ReassignWhatsAppModal";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";
import usePermission from "../../hooks/usePermission";

import { ArrowLeft, MessageSquare } from "lucide-react";
const TicketInfo = ({ contact, ticket, onClick, onTicketUpdate }) => {
	const location = useLocation();
	const history = useHistory();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const queryParams = new URLSearchParams(location.search);
	const fromNoResponse = queryParams.get('from') === 'noresponse';
	// Trocar a conexão: hoje, aberto a quem atende; com a regra nova, tickets.manage_connection.
	const { can } = usePermission();
	const canManageConnection = can("tickets.manage_connection", true);
	const [reassignModalOpen, setReassignModalOpen] = useState(false);
	const { user } = ticket
	const [userName, setUserName] = useState('')
	const [contactName, setContactName] = useState('')
	const [participantsCount, setParticipantsCount] = useState(null)
	const socketManager = useContext(SocketContext);
	// Presence efêmero do contato (typing/recording) — mesmo evento
	// company-X-presence que a lista de tickets já usa (WAHA e LiveChat).
	const [presence, setPresence] = useState(null);
	const presenceTimerRef = useRef(null);

	useEffect(() => {
		if (!ticket?.id) return undefined;
		const companyId = localStorage.getItem("companyId");
		let socket;
		try {
			socket = socketManager.getSocket(companyId);
		} catch {
			return undefined;
		}

		const handlePresence = (data) => {
			if (!data || data.ticketId !== ticket.id) return;
			if (presenceTimerRef.current) {
				clearTimeout(presenceTimerRef.current);
				presenceTimerRef.current = null;
			}
			if (data.presence === "typing" || data.presence === "recording") {
				setPresence(data.presence);
				// safety: limpa sozinho se o "paused" se perder
				presenceTimerRef.current = setTimeout(() => setPresence(null), 10000);
			} else {
				setPresence(null);
			}
		};

		socket.on(`company-${companyId}-presence`, handlePresence);
		return () => {
			socket.off(`company-${companyId}-presence`, handlePresence);
			if (presenceTimerRef.current) clearTimeout(presenceTimerRef.current);
			setPresence(null);
		};
	}, [ticket?.id, socketManager]);

	useEffect(() => {
		if (contact) {
			setContactName(contact.name);
			if(document.body.offsetWidth < 600) {
				if (contact.name.length > 10) {
					const truncatedName = contact.name.substring(0, 10) + '...';
					setContactName(truncatedName);
				}
			}
		}

		if (user && contact) {
			setUserName(`${i18n.t("messagesList.header.assignedTo")} ${user.name}`);
			if(document.body.offsetWidth < 600) {
				setUserName(`${user.name}`);
			}
		} else if (ticket?.aiAgent && contact) {
			setUserName(`${i18n.t("messagesList.header.assignedTo")} ${ticket.aiAgent.name} (IA)`);
			if(document.body.offsetWidth < 600) {
				setUserName(`${ticket.aiAgent.name} (IA)`);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contact, user, ticket?.aiAgent])

	useEffect(() => {
		let cancelled = false;
		if (!contact?.isGroup || !ticket?.id) {
			setParticipantsCount(null);
			return;
		}
		api.get(`/tickets/${ticket.id}/group-info`)
			.then((res) => {
				if (!cancelled) setParticipantsCount(res.data?.participantsCount ?? null);
			})
			.catch(() => { if (!cancelled) setParticipantsCount(null); });
		return () => { cancelled = true; };
	}, [contact?.isGroup, ticket?.id])

	return (
		<>
			<CardHeader
				onClick={onClick}
				style={{ cursor: "pointer", padding: isMobile ? '8px 12px' : '8px 16px' }}
				titleTypographyProps={{ noWrap: true, style: { fontSize: '0.95rem', fontWeight: 500 } }}
				subheaderTypographyProps={{ noWrap: true, style: { fontSize: '0.78rem' } }}
				avatar={
					<Box display="flex" alignItems="center" style={{ gap: 4 }}>
						{isMobile && (
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									history.push("/tickets");
								}}
								style={{ marginRight: 4 }}
							>
								<ArrowLeft />
							</IconButton>
						)}
						<Avatar src={contact.profilePicUrl} alt="contact_image" />
					</Box>
				}
				title={
					<Box display="flex" alignItems="center" style={{ minWidth: 0 }}>
						{/* Só o nome encolhe (com reticências); os ícones ao lado ficam visíveis. */}
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
							{`${contactName} #${ticket.id}`}
						</span>
						{ticket?.whatsapp?.provider === "livechat" && (
							<Tooltip title={`LiveChat — ${ticket.whatsapp.name}`}>
								<span style={{ display: "inline-flex", alignItems: "center", marginLeft: 6, flexShrink: 0 }}>
									<MessageSquare size={14} style={{ color: "#6366f1" }} />
								</span>
							</Tooltip>
						)}
						{fromNoResponse && !ticket.whatsappId && canManageConnection && (
							<Tooltip title="Alterar conexão WhatsApp">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setReassignModalOpen(true);
									}}
									style={{ marginLeft: 8, flexShrink: 0 }}
								>
									<SwapHorizIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				}
				subheader={
					<>
						{contact?.isGroup && participantsCount != null && (
							<span style={{ fontSize: 11, opacity: 0.7, display: "block", lineHeight: 1.2 }}>
								{participantsCount} participantes
							</span>
						)}
						{presence ? (
							<span style={{ color: green[500], fontStyle: "italic", fontWeight: 500 }}>
								{presence === "recording"
									? i18n.t("ticketsListItem.recording")
									: i18n.t("ticketsListItem.typing")}
							</span>
						) : (
							(ticket.user || ticket.aiAgent) && userName
						)}
					</>
				}
			/>
			<ReassignWhatsAppModal
				open={reassignModalOpen}
				onClose={() => setReassignModalOpen(false)}
				ticketId={ticket.id}
				ticketStatus={ticket.status}
				onSuccess={onTicketUpdate}
			/>
		</>
	);
};

export default TicketInfo;
